"use client";

import { useEffect, useState } from "react";

import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  TablePagination,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { axiosRequest } from "../../utility/axios";

import { apiBaseUrl } from "../../constant/endpoint";

import { useSelector } from "react-redux";

import CreateRakeDrawer from "./rakeForm.js";
import RakeViewDialog from "./rakeViewDialog.js";
import { formatISTDateTime } from "../../helper";

export default function Rake() {

  const [rakes, setRakes] =
    useState([]);

  const [selectedRake, setSelectedRake] = useState(null);
  const [openView, setOpenView]         = useState(false);

  const [openDrawer, setOpenDrawer] =
    useState(false);

  const [page, setPage] =
    useState(0);

  const [rowsPerPage, setRowsPerPage] =
    useState(10);

  const [pagination, setPagination] =
    useState({});

  const [dateFrom, setDateFrom]       = useState("");
  const [dateTo, setDateTo]           = useState("");
  const [tableFilter, setTableFilter] = useState("");
  const [tables, setTables]           = useState([]);

  const loginSlice = useSelector(
    (state) => state.loginSlice
  );

  const token = loginSlice.token;

  /* FETCH RAKES */

  const fetchRakes = async () => {

    try {

      let url =
        `${apiBaseUrl}/rake?page=${page + 1}&size=${rowsPerPage}`;

      if (dateFrom)    url += `&date_from=${dateFrom}`;
      if (dateTo)      url += `&date_to=${dateTo}`;
      if (tableFilter) url += `&table_name=${encodeURIComponent(tableFilter)}`;

      const res =
        await axiosRequest(
          "GET",
          url,
          token
        );

      setRakes(
        res?.data?.rakeList || []
      );

      setPagination(
        res?.data
          ?.paginationData || {}
      );

    } catch (err) {

      console.error(err);
    }
  };

  useEffect(() => {
    axiosRequest("GET", `${apiBaseUrl}/tables`, token)
      .then((res) => setTables(res?.data?.data || []))
      .catch(() => setTables([]));
  }, []);

  useEffect(() => {

    fetchRakes();

  }, [page, rowsPerPage, dateFrom, dateTo, tableFilter]);

  return (
    <Box
      sx={{
        width: "100%",
        marginTop: { xs: "55px", md: "95px" },
        pb: 6,
      }}
    >

      <Box
        p={3}
        sx={{
          padding: "32px",
          marginTop: "104px",
        }}
      >

        {/* HEADER */}

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >

          <Box>

            <Typography
              variant="h4"
              fontWeight="bold"
            >
              Rake Management
            </Typography>

            <Typography
              color="text.secondary"
            >
              Track table wise
              chips and tips
            </Typography>

          </Box>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() =>
              setOpenDrawer(true)
            }
            sx={{
              borderRadius: 3,
              height: 45,
            }}
          >
            Create Rake
          </Button>

        </Box>

        {/* FILTERS */}
        <Box display="flex" gap={2} alignItems="center" mb={3} flexWrap="wrap">
          <TextField
            size="small" type="date" label="From"
            InputLabelProps={{ shrink: true }}
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
          />
          <TextField
            size="small" type="date" label="To"
            InputLabelProps={{ shrink: true }}
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Table Name</InputLabel>
            <Select
              label="Table Name"
              value={tableFilter}
              onChange={(e) => { setTableFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="">All</MenuItem>
              {tables.map((t) => (
                <MenuItem key={t._id} value={t.tableName}>{t.tableName}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {(dateFrom || dateTo || tableFilter) && (
            <Button
              size="small" variant="outlined" color="inherit"
              onClick={() => { setDateFrom(""); setDateTo(""); setTableFilter(""); setPage(0); }}
            >
              Clear
            </Button>
          )}
        </Box>

        {/* TABLE */}

        <Paper
          sx={{
            borderRadius: 4,
            overflow: "hidden",
          }}
        >

          <Table>

            <TableHead>

              <TableRow>

                <TableCell>
                  Date
                </TableCell>

                <TableCell>
                  Table Name
                </TableCell>

                <TableCell>
                  Chips In
                </TableCell>

                <TableCell>
                  Tips
                </TableCell>

                <TableCell>
                  Final Rake
                </TableCell>

                <TableCell>
                  Status
                </TableCell>

                <TableCell align="center">
                  Action
                </TableCell>

              </TableRow>

            </TableHead>

            <TableBody>

              {rakes.length >
              0 ? (

                rakes.map(
                  (rake) => (

                    <TableRow
                      key={
                        rake._id
                      }
                    >

                      <TableCell>
                        {formatISTDateTime(rake.date)}
                      </TableCell>

                      <TableCell>
                        {rake.table_name || "—"}
                      </TableCell>

                      <TableCell>
                        {rake.total_in?.toLocaleString() || 0}
                      </TableCell>

                      <TableCell>
                        {rake.total_tips?.toLocaleString() || 0}
                      </TableCell>

                      <TableCell>
                        <Typography fontWeight={700} color="success.main">
                          {rake.rake_amount?.toLocaleString() || 0}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={rake.status}
                          color={
                            rake.status === "OPEN"    ? "warning" :
                            rake.status === "RUNNING" ? "info"    :
                            rake.status === "CLOSED"  ? "success" : "default"
                          }
                        />
                      </TableCell>

                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => { setSelectedRake(rake); setOpenView(true); }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </TableCell>

                    </TableRow>
                  )
                )

              ) : (

                <TableRow>

                  <TableCell
                    colSpan={7}
                    align="center"
                  >
                    No Rake Found
                  </TableCell>

                </TableRow>
              )}

            </TableBody>

          </Table>

        </Paper>

        {/* PAGINATION */}

        <TablePagination
          component="div"
          count={
            pagination?.totalRecords ||
            0
          }
          page={page}
          onPageChange={(
            e,
            newPage
          ) =>
            setPage(newPage)
          }
          rowsPerPage={
            rowsPerPage
          }
          onRowsPerPageChange={(
            e
          ) => {

            setRowsPerPage(
              parseInt(
                e.target.value,
                10
              )
            );

            setPage(0);
          }}
          rowsPerPageOptions={[
            10,
            20,
            50,
          ]}
        />

        {/* DRAWER */}

        <CreateRakeDrawer
          open={openDrawer}
          onClose={() => setOpenDrawer(false)}
          refresh={fetchRakes}
        />

        <RakeViewDialog
          open={openView}
          rake={selectedRake}
          onClose={() => setOpenView(false)}
          refresh={fetchRakes}
        />

      </Box>

    </Box>
  );
}
